<template>
  <div>
    <b-card no-body class="p-2">
      <b-alert show variant="info" class="mb-2">
        Create matching Products and Prices in the
        <a href="https://dashboard.stripe.com/products" target="_blank" rel="noopener noreferrer">Stripe Dashboard</a>,
        then paste the Price IDs below for paid checkout.
      </b-alert>

      <validation-observer ref="formObserver">
        <b-form @submit.prevent="savePlan">
          <b-row>
            <b-col md="6">
              <b-form-group label="Plan Name">
                <validation-provider #default="{ errors }" name="Plan Name" rules="required">
                  <b-form-input v-model="form.name" placeholder="e.g. Standard" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Currency">
                <b-form-input v-model="form.currency" placeholder="usd" maxlength="8" />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Monthly price">
                <validation-provider #default="{ errors }" name="Monthly price" rules="required">
                  <b-form-input v-model="form.price" type="number" min="0" step="0.01" placeholder="0 for free tier" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Yearly price">
                <b-form-input v-model="form.price_yearly" type="number" min="0" step="0.01" placeholder="Optional" />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Duration (days)">
                <validation-provider #default="{ errors }" name="Duration" rules="required">
                  <b-form-input v-model="form.duration" type="number" min="1" placeholder="30" />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Status">
                <v-select
                  v-model="selectedStatus"
                  :options="statusOptions"
                  :clearable="false"
                  label="label"
                />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Mark as popular">
                <b-form-checkbox v-model="form.is_popular" switch>
                  Show popular badge on advertiser billing page
                </b-form-checkbox>
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Stripe Price ID (monthly)">
                <b-form-input
                  v-model="form.stripe_price_id_monthly"
                  placeholder="price_..."
                />
              </b-form-group>
            </b-col>

            <b-col md="6">
              <b-form-group label="Stripe Price ID (yearly)">
                <b-form-input
                  v-model="form.stripe_price_id_yearly"
                  placeholder="price_..."
                />
              </b-form-group>
            </b-col>

            <b-col md="12">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <h5 class="mb-0 font-weight-bolder">Plan Features</h5>
                <b-button size="sm" variant="primary" @click="showFeatureModal = true">
                  Add New Feature
                </b-button>
              </div>
              <b-table
                :items="featureRows"
                :fields="featureFields"
                small
                responsive
                show-empty
                empty-text="No features added yet."
                class="mb-1"
              >
                <template #cell(actions)="row">
                  <feather-icon
                    icon="Trash2Icon"
                    size="16"
                    class="cursor-pointer text-danger"
                    @click="removeFeature(row.index)"
                  />
                </template>
              </b-table>
            </b-col>

            <b-col md="12" class="d-flex justify-content-end">
              <b-button type="button" variant="outline-secondary" class="mr-1" @click="$router.push({ name: 'pages-pricing' })">
                Cancel
              </b-button>
              <b-button type="submit" variant="primary" :disabled="isSaving || isLoading">
                <b-spinner v-if="isSaving" small class="mr-50" />
                {{ isEdit ? 'Update Plan' : 'Create Plan' }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-card>

    <b-modal v-model="showFeatureModal" title="Add New Feature" centered @hidden="resetFeatureForm">
      <b-form-group label="Feature name">
        <b-form-input v-model="newFeatureName" placeholder="e.g. Priority support" />
      </b-form-group>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showFeatureModal = false">Cancel</b-button>
        <b-button variant="primary" @click="addFeature">Save Feature</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BCard,
  BCol,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BRow,
  BSpinner,
  BTable,
  BModal,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import vSelect from 'vue-select'
import { adminPanelApi } from '@/services/adminPanelApi'
import { unwrapAdminResponse } from '@/utils/adminApiHelpers'

export default {
  components: {
    BAlert,
    BButton,
    BCard,
    BCol,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BRow,
    BSpinner,
    BTable,
    BModal,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
  data() {
    return {
      form: {
        name: '',
        price: '',
        price_yearly: '',
        currency: 'usd',
        duration: '30',
        features: '',
        is_popular: false,
        stripe_price_id_monthly: '',
        stripe_price_id_yearly: '',
      },
      statusOptions: [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false },
      ],
      selectedStatus: { label: 'Active', value: true },
      isSaving: false,
      isLoading: false,
      featureRows: [],
      featureFields: [
        { key: 'name', label: 'Feature' },
        { key: 'actions', label: 'Actions', thClass: 'text-center', tdClass: 'text-center' },
      ],
      showFeatureModal: false,
      newFeatureName: '',
    }
  },
  computed: {
    isEdit() {
      return !!this.$route.params.id
    },
  },
  mounted() {
    if (this.isEdit) this.fetchPlanById()
  },
  methods: {
    parseFeatures(text) {
      return String(text || '')
        .split('\n')
        .map(item => item.trim())
        .filter(Boolean)
    },
    syncFeatureRowsFromText() {
      this.featureRows = this.parseFeatures(this.form.features).map(name => ({ name }))
    },
    syncTextFromFeatureRows() {
      this.form.features = this.featureRows.map(row => row.name).join('\n')
    },
    addFeature() {
      const name = (this.newFeatureName || '').trim()
      if (!name) return
      this.featureRows.push({ name })
      this.syncTextFromFeatureRows()
      this.showFeatureModal = false
      this.resetFeatureForm()
    },
    removeFeature(index) {
      this.featureRows.splice(index, 1)
      this.syncTextFromFeatureRows()
    },
    resetFeatureForm() {
      this.newFeatureName = ''
    },
    applyPlanToForm(plan) {
      this.form.name = plan.name || ''
      this.form.price = plan.price ?? ''
      this.form.price_yearly = plan.price_yearly ?? ''
      this.form.currency = plan.currency || 'usd'
      this.form.duration = plan.duration || plan.duration_days || 30
      this.form.features = Array.isArray(plan.features)
        ? plan.features.join('\n')
        : (plan.features || '')
      this.syncFeatureRowsFromText()
      this.form.is_popular = !!plan.is_popular
      this.form.stripe_price_id_monthly = plan.stripe_price_id_monthly || ''
      this.form.stripe_price_id_yearly = plan.stripe_price_id_yearly || ''
      const active = plan.is_active != null ? !!plan.is_active : (plan.status == 1 || plan.status === true)
      this.selectedStatus = active ? this.statusOptions[0] : this.statusOptions[1]
    },
    async fetchPlanById() {
      this.isLoading = true
      try {
        const res = await adminPanelApi.plan(this.$route.params.id)
        const plan = unwrapAdminResponse(res)
        if (plan) this.applyPlanToForm(plan)
      } catch {
        this.$swal({
          title: 'Error',
          text: 'Unable to load plan',
          icon: 'error',
          customClass: { confirmButton: 'btn btn-primary' },
          buttonsStyling: false,
        })
        this.$router.push({ name: 'pages-pricing' })
      } finally {
        this.isLoading = false
      }
    },
    buildPayload() {
      const payload = {
        name: this.form.name,
        price: Number(this.form.price),
        price_yearly: this.form.price_yearly === '' ? null : Number(this.form.price_yearly),
        currency: (this.form.currency || 'usd').toLowerCase(),
        duration: Number(this.form.duration),
        features: this.parseFeatures(this.form.features),
        is_popular: !!this.form.is_popular,
        is_active: !!this.selectedStatus?.value,
        stripe_price_id_monthly: this.form.stripe_price_id_monthly?.trim() || null,
        stripe_price_id_yearly: this.form.stripe_price_id_yearly?.trim() || null,
      }
      if (this.isEdit) payload.id = Number(this.$route.params.id)
      return payload
    },
    savePlan() {
      this.$refs.formObserver.validate().then(async success => {
        if (!success) return
        this.isSaving = true
        try {
          const payload = this.buildPayload()
          if (this.isEdit) {
            await adminPanelApi.updatePlan(payload)
          } else {
            await adminPanelApi.createPlan(payload)
          }
          this.$swal({
            title: 'Success',
            text: this.isEdit ? 'Plan updated successfully' : 'Plan created successfully',
            icon: 'success',
            customClass: { confirmButton: 'btn btn-primary' },
            buttonsStyling: false,
          })
          this.$router.push({ name: 'pages-pricing' })
        } catch (error) {
          const message = error?.response?.data?.message
            || (this.isEdit ? 'Unable to update plan' : 'Unable to create plan')
          this.$swal({
            title: 'Error',
            text: message,
            icon: 'error',
            customClass: { confirmButton: 'btn btn-primary' },
            buttonsStyling: false,
          })
        } finally {
          this.isSaving = false
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
